/* English (default / fallback) — see .skills/i18n-workflow/SKILL.md */
export const en = {
  common: {
    brand: 'Nexnovar',
    nav: {
      product: 'Product',
      login: 'Sign in',
      back: 'Back',
    },
    cta: {
      primary: 'Get started',
      secondary: 'Learn more',
      example: 'See a complete example →',
      login: 'Sign in to Nexnovar',
    },
    footer: {
      rights: 'All rights reserved.',
    },
  },
  home: {
    stats: {
      eyebrow: 'By the numbers',
      title: 'Trust infrastructure that scales',
      items: [
        { value: 1247, suffix: '', label: 'Decisions restored' },
        { value: 100, suffix: '%', label: 'Evidence complete' },
        { value: 3, suffix: 's', label: 'Evidence package generated' },
      ],
    },
    painPoints: {
      eyebrow: 'The reality',
      title: 'Evidence lives in five systems that don’t talk to each other',
      body: 'Compliance officers piece together audit responses from email threads, shared drives, archived PDFs, and spreadsheets — every request starts a new hunt.',
      essence:
        'The question is not whether the institution is compliant, but whether it can prove it.',
      timeline: {
        eyebrow: 'Compliance officer · Tuesday 3:42 PM',
        items: [
          'Slack #compliance: "Can someone confirm the KYC tier for #4219?"',
          'Email · Outside counsel · 2 days ago · still unread',
          'Spreadsheet · Issuance-tracker-Q3.xlsx · 14 tabs · last edit 6h ago',
          'Notion · "Regulator feedback (TBD)" · empty',
        ],
      },
      bodyAlt:
        'The longer the hunt, the higher the fine risk — and the less time for the work that actually matters.',
      narrative: {
        businessOfficer: {
          eyebrow: 'Business decision-makers',
          quote:
            'Six months from now, can I reconstruct why I approved this loan? I have no idea — the notes are scattered across email, spreadsheets, and my memory.',
        },
        partner: {
          eyebrow: 'Customers & partners',
          quote:
            'Every onboarding starts with the same six forms. Every quarter, the same documents are re-requested by a different team.',
        },
      },
    },
    cta: {
      title: 'Make trust evidence a manageable asset',
      body: 'Stop assembling audit responses under deadline pressure. Start accumulating them the moment a decision is made.',
      primaryCta: 'Sign in to Nexnovar',
      contactPrompt: 'Questions? Email us at',
    },
    hero: {
      eyebrow: 'Trust Operating System',
      title: 'Nexnovar',
      lede: 'Making Trust a Manageable Asset',
      tagline: 'The decision evidence automation platform built for audit teams.',
      subtitle:
        'Whether it is banking compliance audits, cross-border trade documentation, or research institution credential verification — Nexnovar transforms trust evidence into accumulable, controllable, and callable assets.',
      tags: {
        banking: 'Banking',
        trade: 'Trade',
        research: 'Research',
      },
      contactPrompt: 'Questions? Email us at',
    },
    intro: {
      kicker: 'Nexnovar',
      title: 'Trust Operating System',
      lede: 'Turning Trust Evidence into accumulable, controllable, and callable assets.',
      pillars: [
        {
          title: 'Trust Assetization',
          body: 'Upgrading KYC, credentials, and authorizations from one-time consumables into reusable digital assets.',
        },
        {
          title: 'Verify Once, Reuse Everywhere',
          body: 'Verification results are accessible by authorized institutions — no need to resubmit.',
        },
        {
          title: 'Authorization is the Boundary',
          body: 'Every data access requires explicit authorization, with complete evidence chains while data stays in place.',
        },
        {
          title: 'Compliance by Default',
          body: 'Policy changes scanned in seconds, expiration signals managed automatically, compliance as the default state.',
        },
      ],
      footnote:
        'Banking, cross-border trade, research institutions, government governance — different scenarios, same trust logic.',
      // roleMapping:
      //   'For customers we deliver in 4 roles: Trust Identity (NovarPass), Trust Assets (NovarVault), Authorization & Records (NovarConsent + NovarAnchor), and AI + LCM + Audit (NovarAIScan + NovarLCM + NovarAudit).',
      contactPrompt: 'Want to learn more?',
    },
    capabilities: {
      eyebrow: 'Platform',
      kicker: 'Nexnovar Platform',
      title: '7 Core Capabilities',
      lede: 'Complete lifecycle management of trust assets',
      items: [
        { title: 'Verify once, reuse everywhere' },
        { title: 'Authorization is boundary, evidence has ID' },
        { title: 'Personal authorization, data stays put' },
        { title: 'Decisions immutable, permanently traceable' },
        { title: 'Policy changes scanned in seconds' },
        { title: 'Expiration signals auto-consolidated' },
      ],
      auditTitle: 'One-click self-certification',
      auditBody: 'Historical decisions, self-certified in one click',
    },
    synergy: {
      kicker: 'Capability Synergy',
      title: 'How the 7 Capabilities Work Together',
      lede: 'Complete closed loop from trust establishment to audit certification',
      modules: [
        {
          name: 'NovarPass',
          purpose: 'Establish trust identity',
          tags: ['passport', 'diploma'],
        },
        {
          name: 'NovarVault',
          purpose: 'Store trust assets',
          tags: ['contract', 'research', 'invoice'],
        },
        {
          name: 'NovarConsent',
          purpose: 'Control authorization',
          tags: ['scope-bound', 'auditable'],
        },
        {
          name: 'NovarAnchor',
          purpose: 'Anchor trust decisions',
          tags: ['immutable', 'timestamped'],
        },
        {
          name: 'NovarAIScan',
          purpose: 'Monitor policy changes',
          tags: ['policy diff', 'anomaly scan'],
        },
        { name: 'NovarLCM', purpose: 'Maintain lifecycle', tags: ['expiry alerts', 'renewals'] },
        {
          name: 'NovarAudit',
          purpose: 'Prepare audit certification',
          tags: ['self-attest', '3s package'],
        },
      ],
    },
    synergyAnimation: {
      title: 'How the 7 Capabilities Work Together',
      subline: 'A complete closed loop — from trust establishment to audit certification.',
      avatar: 'Alice · Personal Account',
      stages: {
        s1: 'The title sets the stage — seven capabilities, one closed loop.',
        s2: 'Title particles fly apart — NovarPass (blue) and NovarVault (purple) crystallize on either side.',
        s3: 'Organization enters at center bottom — three energy ripples rise and ask for evidence.',
        s4: 'Flow lines from Pass and Vault deliver NovarConsent — the ✓ badge lands on the requester.',
        s5: 'Two NovarAnchors seal the loop — Pass, Vault, and requester are bound forever.',
        s6: 'The NovarLCM ring wraps everything and starts spinning — NovarAIScan appears on the right.',
        s7: 'Dashed comm line pulses between ring and AIAgent — payloads rotate: expiry check, renewal, policy diff, anchor verify, anomaly scan.',
        s8: 'When the payload is "audit" — three red rings fire and the NovarAudit badge lights up; AIAgent highlights in sync.',
      },
    },
    scenario: {
      kicker: 'Complete Scenario',
      title: 'A Day in the Life: A Bank Using Nexnovar',
      steps: [
        {
          title: 'Customer Onboarding: NovarPass Completes KYC',
          body: 'Customer Alice opens an account for the first time; after one KYC verification, results are stored in NovarVault.',
        },
        {
          title: 'Loan Application: NovarConsent Records Authorization',
          body: 'Alice authorizes the bank to verify her credit status — institution only receives verification status, not raw data.',
        },
        {
          title: 'Approval Decision: NovarAnchor Anchors Evidence',
          body: 'When John approves the loan, the decision is cryptographically anchored — including the policy version and timestamp at that time.',
        },
        {
          title: 'AML Policy Update: NovarAIScan Auto-Scans',
          body: 'RBNZ publishes new policy; NovarAIScan identifies all affected customers in seconds.',
        },
        {
          title: 'Customer Maintenance: NovarLCM Monitors Expiration',
          body: '30 days before Alice’s KYC expires, NovarLCM auto-alerts and initiates renewal process.',
        },
        {
          title: 'RBNZ Audit: NovarAudit One-Click Generates Evidence Package',
          body: '1,247 decisions restored in 3 seconds with complete evidence chains, verifiable independently by auditors.',
        },
      ],
    },
    auditNotice: {
      kicker: 'Monday 9:30 AM',
      title: 'RBNZ Sends an Audit Notice',
      emailBadge: '📨 Urgent Audit Notice',
      toLabel: 'To:',
      toValue: 'Bob Wilson (Compliance Officer)',
      fromLabel: 'From:',
      fromValue: 'RBNZ Audit Department',
      subject: '2023 Q4 Loan Approval Compliance Review',
      body: 'Please submit the compliance evidence package for all Q4 2023 loan approval decisions within 7 business days.',
      totalLabel: 'Total decisions',
      totalValue: '1,247',
      fineRisk: 'Fine Risk',
      fineAmount: '5M',
      fineCurrency: 'NZD (maximum)',
      caption: '"The email no compliance team wants to receive."',
    },
    bobsDilemma: {
      kicker: 'Bob’s Dilemma',
      title: '415 Hours of Work, 7 Days to Complete',
      systemsLabel: 'Systems Bob needs to search:',
      systems: [
        'Legacy Email System',
        'Loan Management System',
        'PDF Archive',
        'Shared Drive',
        'Excel Spreadsheets',
      ],
      required: 'Time Required',
      requiredValue: 'hours',
      requiredCalc: '= 1,247 × 20 minutes',
      available: 'Time Available',
      availableValue: 'days = 168 hours',
      gapLabel: 'Gap',
      gapValue: '247 hours',
      footnote: 'Even if he never slept, he couldn’t finish.',
    },
    cost: {
      kicker: 'The Cost of Compliance Failure',
      title: 'It\'s Not Just About Fines',
      items: [
        { label: 'AML Failure', amount: '1.3B' },
        { label: 'Customer Misguidance', amount: '2.5B' },
      ],
      footnote1: 'Fines are the visible cost.',
      footnote2: 'Reputation loss, customer churn, regulatory escalation are hidden costs.',
      quote:
        '"Compliance is a bank’s bottom line. The core of compliance is: you can prove you did what you were supposed to do."',
    },
    novaAudit: {
      kicker: 'NovarAudit · Core Capability',
      title: '1,247 Decisions in 3 Seconds',
      stat: '3s',
      statLabel: 'Complete evidence package for 1,247 decisions',
      items: [
        { value: '1,247', label: 'Total Decisions' },
        { value: '100%', label: 'Traceable' },
        { value: '2.4', suffix: 'GB', label: 'Evidence Size' },
      ],
    },
    decisionDetail: {
      kicker: 'NovarAudit · Decision Details',
      title: 'Every Decision Restored to Its Complete State at the Time',
      fields: [
        { label: 'Decision ID', value: '#DEC-2023-04217' },
        { label: 'Decision Time', value: '2023-10-15 14:23:47 UTC+13' },
        { label: 'Decision Maker', value: 'John Smith (Senior Credit Manager)' },
        { label: 'Decision Content', value: 'Approved NZD 450,000 Mortgage' },
        {
          label: 'Evidence Source',
          value: 'Loan Management System v3.2 (read-only)',
        },
        {
          label: 'Policy Version at Time',
          value: 'Loan Policy v1.2.0 (Effective 2023-10-01)',
        },
        {
          label: 'Anchor Hash',
          value: '0x9f4a2c8b7e1d...f3a',
        },
        { label: 'Trust Signature', value: 'Verified · Ed25519' },
      ],
      actions: ['View All 1,247', 'Export Report'],
    },
    trustVerify: {
      kicker: 'NovarAudit · Trust Verification',
      title: 'Auditors Don\'t Need to Trust Bob — They Can Verify Themselves',
      registryTitle: 'Trust Registry Verification',
      placeholder: 'Enter Anchor Hash',
      verify: 'Verify',
      checks: [
        { question: 'Was decision content tampered with?', answer: 'No' },
        { question: 'Was policy version in effect?', answer: 'Yes (v1.2.0)' },
        { question: 'Is decision maker authentic?', answer: 'Yes (John Smith)' },
        { question: 'Is evidence chain complete?', answer: 'Yes' },
      ],
      quote: '"Trust is verifiable, not through belief but through proof."',
    },
    auditComplete: {
      kicker: 'NovarAudit · Complete',
      title: '9:33 AM — All Evidence Package Submitted',
      confirmation: 'RBNZ Audit Confirmation:',
      results: [
        '1,247 decisions — 100% traceable',
        '0 compliance issues',
        'Audit passed',
        '0 fines',
      ],
      before: '9:30 receive notification',
      after: '9:33 submit evidence package',
      footnote: '3 minutes vs weeks traditionally',
      quote: 'Compliance is no longer a bank\'s nightmare, but a routine part of daily operations.',
      boundary:
        'Help auditors find anomalies faster and more precisely — not replace their professional judgment.',
      backToHome: 'Back to home',
    },
    useCases: {
      kicker: 'Use Cases',
      title: 'One Trust Infrastructure, Multiple Industries',
      items: [
        {
          industry: 'Banking Audit',
          topic: 'Compliance Self-Cert',
          body: 'During RBNZ audit, generate evidence package in 3 seconds.',
        },
        {
          industry: 'Cross-Border Trade',
          topic: 'Document Flow',
          body: 'Exporter submits once, multiple parties accept.',
        },
        {
          industry: 'Research Collab',
          topic: 'Credential Reuse',
          body: 'Researcher verifies once, reused across projects.',
        },
        {
          industry: 'Government',
          topic: 'Multi-Dept Coord',
          body: 'Departments share info, data stays while verification flows.',
        },
        {
          industry: 'Insurance Audit',
          topic: 'Claims Evidence',
          body: 'Every claim decision has its evidence chain — ready for regulator review.',
        },
        {
          industry: 'Healthcare',
          topic: 'Care Audit',
          body: 'Treatment decisions and consents reconstructable on demand.',
        },
        {
          industry: 'Pharma',
          topic: 'Trial Compliance',
          body: 'Protocol version, consent signatures, and decisions captured at every step.',
        },
      ],
    },
    thankYou: {
      title: 'Thank You',
      lede: 'Making Trust a Manageable Asset',
      tagline: 'Trust, No Compromise.',
      contactPrompt: 'Questions? Email us at',
    },
    architecture: {
      eyebrow: 'How we integrate',
      title: 'Nexnovar is an overlay, not a replacement.',
      lede: 'Your core systems stay the way they are. We plug in through APIs — your data stays in your environment; only authorized callers see the fingerprints they were granted.',
      steps: [
        {
          number: '01',
          title: 'Your core systems stay intact',
          body: 'Banking cores, insurance claims, hospital systems, government budgets — none of them change.',
        },
        {
          number: '02',
          title: 'We plug in through APIs',
          body: 'Adapter code (yours or ours) wraps the existing system and exposes the events Nexnovar needs.',
        },
        {
          number: '03',
          title: 'Identity and assets register as evidence',
          body: 'Identity evidence (passports, KYC) and asset evidence (contracts, invoices, credentials) sit in Nexnovar under cryptographic signatures.',
        },
        {
          number: '04',
          title: 'Going out stays under consent',
          body: 'Authorized callers read only the fingerprints you grant them — and that read is logged as another use.',
        },
      ],
      footnote:
        'Same gate in, same gate out. Nothing leaves your environment without explicit consent.',
    },
    boundaryStatement: {
      eyebrow: 'Where we stop',
      title: 'What we don\'t do.',
      items: [
        { text: 'We don\'t ask you to rebuild existing systems.' },
        { text: 'We don\'t replace your professional audit judgment.' },
        { text: 'We don\'t take your raw data out of your environment.' },
        { text: 'We don\'t generate credit scores or make credit decisions.' },
        { text: 'We don\'t collect external data (credit bureaus, public records, lawsuits).' },
      ],
      quote: 'Data sovereignty: raw data stays inside the customer environment.',
    },
    positioning: {
      kicker: 'What we are — and aren’t',
      title: 'Nexnovar is not a credit scoring system. Not a data lake. Not generic AI.',
      lede: 'Three categories people confuse us with. Here is the difference, on the four dimensions that matter.',
      headers: {
        question: 'Question',
        credit: 'Credit Scoring',
        data: 'Data Lake',
        ai: 'Generic AI',
        nexnovar: 'Nexnovar',
      },
      rows: [
        {
          question: 'Where does the data live?',
          credit: 'External bureau pulls',
          data: 'Centralized copies',
          ai: 'Whatever you ingest',
          nexnovar: 'Stays in your environment',
        },
        {
          question: 'Who can read it?',
          credit: 'The bureau + you',
          data: 'Platform operators',
          ai: 'Model providers',
          nexnovar: 'Only authorized callers, only fingerprints',
        },
        {
          question: 'What does it decide?',
          credit: 'A credit score',
          data: 'Nothing — it stores',
          ai: 'Whatever it’s prompted to',
          nexnovar: 'Nothing — it records and verifies',
        },
        {
          question: 'What does it produce?',
          credit: 'A number, opaque',
          data: 'Query results',
          ai: 'Generated text / judgments',
          nexnovar: 'An evidence package, signed and timestamped',
        },
      ],
    },
    dataApproach: {
      eyebrow: 'Data approach',
      title: 'Raw data never leaves your environment.',
      lede: 'We store three kinds of fingerprints — nothing more. Three ways to connect — choose what fits your stack.',
      doNotItems: [
        'We don\'t store original documents.',
        'We don\'t pull from credit bureaus, registries, or external sources.',
        'We don\'t output a credit score or any third-party rating.',
        'We don\'t train models on your data.',
      ],
      weDoItems: [
        'Decision fingerprints — a hash of the decision contents at the moment it was made.',
        'Evidence-source fingerprints — a hash pointing to the source system that originated the decision.',
        'Authorization signatures — cryptographic signature proving who authorized what.',
      ],
      connectMethods: [
        {
          name: 'System read-only',
          useCase: 'For systems of record',
          description:
            'A read-only database connection that streams decision events as they happen.',
        },
        {
          name: 'API',
          useCase: 'For modern stacks',
          description: 'REST endpoints that your applications call at decision time.',
        },
        {
          name: 'File import',
          useCase: 'For batch / legacy systems',
          description:
            'Drop decisions or evidence as files on a schedule; we extract the fingerprints.',
        },
      ],
    },
    capabilityBoundary: {
      eyebrow: 'What we do — and don’t',
      colCan: 'What we do',
      colCannot: 'What we do not',
      title: 'Clear boundaries, no surprises.',
      canDo: [
        'Restore an evidence chain for 1,247 decisions in 3 seconds.',
        'Auto-flag anomalies in your existing decision flow.',
        'Maintain an immutable audit trail with cryptographic signatures.',
        'Reuse verified evidence across authorized institutions.',
        'Scan regulatory changes within 24 hours and map impact.',
      ],
      cannotDo: [
        'Judge whether a decision was correct.',
        'Replace your auditors’ professional judgment.',
        'Output a credit score or third-party rating.',
        'Ask you to rebuild your existing systems.',
        'Take raw data out of your environment.',
      ],
      quote:
        'Help auditors find anomalies faster and more precisely — not replace their professional judgment.',
    },
    complianceReady: {
      eyebrow: 'Compliance posture',
      title: 'Built for regulated environments.',
      lede: 'Pending counsel sign-off — these statements are placeholders until legal review.',
      items: [
        {
          standard: 'FMA Audit Quality (NZ)',
          status: 'Pending counsel review',
          note: 'Financial Markets Authority audit standards.',
        },
        {
          standard: 'Privacy Act 2020 (NZ)',
          status: 'Pending counsel review',
          note: 'New Zealand privacy principles.',
        },
        {
          standard: 'GDPR (EU)',
          status: 'Pending counsel review',
          note: 'Right-to-erasure, data minimization, on-shore deployments available.',
        },
        {
          standard: 'ISO 27001',
          status: 'Pending counsel review',
          note: 'Information security management certification.',
        },
      ],
    },
    governancePolicy: {
      eyebrow: 'Extensibility',
      title: 'Governance Policy — your rules, your industry.',
      lede: 'Every industry has its own governance rules. We give you a place to define them — and we’re building a shared library of rules from the community.',
      pillars: {
        selfDefined: {
          title: 'Self-defined policy',
          body: 'Based on the compliance requirements in your scenario, define a governance policy that fits you.',
        },
        library: {
          title: 'Knowledge library (reserved)',
          body: 'In future, a community-built library of governance rules you can adopt.',
          badge: 'Coming soon · Reserved for POC stage',
        },
      },
      footnote: 'This layer is reserved for POC release; the community library itself ships later.',
    },
  },
  login: {
    title: 'Sign in to Nexnovar',
    subtitle: 'Use your work email. Single sign-on is enabled for enterprise accounts.',
    form: {
      emailLabel: 'Work email',
      emailPlaceholder: 'you@company.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Keep me signed in',
      submit: 'Sign in',
      forgotPassword: 'Forgot password?',
      backToHome: 'Back to home',
    },
    sso: {
      divider: 'or',
      google: 'Continue with Google',
      microsoft: 'Continue with Microsoft',
    },
    note: {
      title: 'Heads up',
      body: 'Authentication is not yet wired to the backend. This page is the future entry point and renders the sign-in UI only.',
    },
    footer: 'Need access? Contact your workspace administrator.',
    contactPrompt: 'Want to learn more?',
    aside: {
      title: 'Compliance, made programmatic.',
      body: 'Nexnovar turns regulatory complexity into auditable, programmatic trust.',
      list: {
        auditTrail: 'Single audit trail your regulator can read.',
        reserveAttestations: 'Real-time reserve attestations.',
        policyAsCode: 'Policy-as-code at signing time.',
      },
    },
  },
  notFound: {
    title: '404',
    body: "We couldn't find that page.",
    cta: 'Back to home',
  },
};

export type Resources = typeof en;
